import type { ParsedCvData } from "@tabbio/types";

/**
 * Body-evidence accounting for a parsed CV.
 *
 * Shared by the API (parse acceptance, destructive replacement guard, paid
 * skill source check) and the apps (client-side refusal to replace a CV with
 * header-only data) so every gate agrees on what "a CV with content" means.
 *
 * Privacy: this module only ever produces counts and booleans. It never
 * returns or logs text, names, contact details, or filenames.
 */

export interface CvBodyEvidenceCounts {
  experienceCount: number;
  educationCount: number;
  certificationCount: number;
  projectCount: number;
  highlightCount: number;
  skillCount: number;
  languageCount: number;
  summaryLength: number;
  hasTitle: boolean;
}

export type CvCompletenessVerdict = "complete" | "minimal" | "header_only";

export interface CvCompletenessAssessment {
  counts: CvBodyEvidenceCounts;
  /** At least one experience, education, certification, project, or highlight. */
  hasSubstantiveBody: boolean;
  /**
   * Substantive body, or a real summary / skills list. A contact header alone
   * (name, email, phone, title, location) never qualifies.
   */
  hasMeaningfulEvidence: boolean;
  verdict: CvCompletenessVerdict;
}

/** A summary shorter than this reads like a headline echo, not a profile. */
export const MIN_MEANINGFUL_SUMMARY_LENGTH = 40;
/** Fewer skills than this is not a skills section, it is a stray keyword. */
export const MIN_MEANINGFUL_SKILL_COUNT = 3;

/** Any object shaped like a parsed CV; identity fields are ignored. */
type ParsedCvLike = Partial<ParsedCvData>;

function arrayLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function trimmedLength(value: unknown): number {
  return typeof value === "string" ? value.trim().length : 0;
}

export function countCvBodyEvidence(
  parsed: ParsedCvLike | null | undefined,
): CvBodyEvidenceCounts {
  const source = parsed ?? {};
  return {
    experienceCount: arrayLength(source.experience),
    educationCount: arrayLength(source.education),
    certificationCount: arrayLength(source.certifications),
    projectCount: arrayLength(source.featuredProjects),
    highlightCount: arrayLength(source.careerHighlights),
    skillCount: arrayLength(source.skills),
    languageCount: arrayLength(source.languages),
    summaryLength: trimmedLength(source.summary),
    hasTitle: trimmedLength(source.title) > 0,
  };
}

/** Number of body records across the sections a CV is judged by. */
export function countSubstantiveCvRecords(counts: CvBodyEvidenceCounts): number {
  return (
    counts.experienceCount +
    counts.educationCount +
    counts.certificationCount +
    counts.projectCount +
    counts.highlightCount
  );
}

export function hasSubstantiveCvBody(counts: CvBodyEvidenceCounts): boolean {
  return countSubstantiveCvRecords(counts) > 0;
}

export function hasMeaningfulCvEvidence(counts: CvBodyEvidenceCounts): boolean {
  return (
    hasSubstantiveCvBody(counts) ||
    counts.summaryLength >= MIN_MEANINGFUL_SUMMARY_LENGTH ||
    counts.skillCount >= MIN_MEANINGFUL_SKILL_COUNT
  );
}

export function assessCvCompleteness(
  parsed: ParsedCvLike | null | undefined,
): CvCompletenessAssessment {
  const counts = countCvBodyEvidence(parsed);
  const substantive = hasSubstantiveCvBody(counts);
  const meaningful = hasMeaningfulCvEvidence(counts);
  return {
    counts,
    hasSubstantiveBody: substantive,
    hasMeaningfulEvidence: meaningful,
    verdict: substantive ? "complete" : meaningful ? "minimal" : "header_only",
  };
}
